import View from './previewView.js';
import icons from 'url:../../img/icons.svg';

class RecipeView extends View {
  _parentElement = document.querySelector('.recipe-window .recipe');
  _errorMessage = 'We could not find that recipe. Please try another one!';
  _message = '';

  _overlay = document.querySelector('.recipe-overlay');
  _window = document.querySelector('.recipe-window');
  _btnClose = document.querySelector('.btn--close-recipe');

  constructor() {
    super();
    this._init();
  }

  _init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setupCloseHandlers());
    } else {
      this._setupCloseHandlers();
    }
  }

  _setupCloseHandlers() {
    // Close on X button click (top-right button)
    if (this._btnClose) {
      this._btnClose.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    // Close on overlay click (outside recipe card) - Method A
    if (this._overlay) {
      this._overlay.addEventListener('click', (e) => {
        // Only close if clicking directly on overlay, not on window
        if (e.target === this._overlay) {
          this.close();
        }
      });
    }

    // Close on clicking search results area (not on recipe cards) - Method B
    const searchResults = document.querySelector('.search-results');
    if (searchResults) {
      searchResults.addEventListener('click', (e) => {
        // Don't close if clicking a recipe card (let hash change handle it)
        if (e.target.closest('.preview')) {
          return;
        }
        // Close if clicking empty area in search results
        if (!this._window.classList.contains('hidden')) {
          this.close();
        }
      });
    }

    // Close on clicking bookmarks area (not on bookmark cards) - Method B
    const bookmarks = document.querySelector('.bookmarks');
    if (bookmarks) {
      bookmarks.addEventListener('click', (e) => {
        // Don't close if clicking a bookmark (let hash change handle it)
        if (e.target.closest('.preview')) {
          return;
        }
        // Close if clicking empty area in bookmarks
        if (!this._window.classList.contains('hidden')) {
          this.close();
        }
      });
    }

    // Close on clicking anywhere outside modal - Method C
    document.addEventListener('click', (e) => {
      // Only close if:
      // 1. Modal is open
      // 2. Click is outside the window
      // 3. Click is not on a preview card (those should open recipe)
      // 4. Click is not on the close button (handled separately)
      if (!this._window.classList.contains('hidden') &&
          !this._window.contains(e.target) &&
          !e.target.closest('.preview') &&
          e.target !== this._btnClose &&
          !e.target.closest('.btn--close-recipe')) {
        this.close();
      }
    });
  }

  open() {
    if (this._overlay && this._window) {
      this._overlay.classList.remove('hidden');
      this._window.classList.remove('hidden');
    }
  }

  close() {
    if (this._overlay && this._window) {
      this._overlay.classList.add('hidden');
      this._window.classList.add('hidden');
      // Clear hash to close modal
      window.history.pushState(null, '', window.location.pathname);
    }
  }

  addHandlerRender(handler) {
    ['hashchange', 'load'].forEach(ev => window.addEventListener(ev, handler));
  }

  addHandlerUpdateServings(handler) {
    this._parentElement.addEventListener('click', function (e) {
      const btn = e.target.closest('.btn--tiny');
      if (!btn) return;
      const { updateTo } = btn.dataset;
      if (+updateTo > 0) handler(+updateTo);
    });
  }

  addHandlerAddBookmark(handler) {
    this._parentElement.addEventListener('click', function (e) {
      const btn = e.target.closest('.btn--bookmark');
      if (!btn) return;
      handler();
    });
  }

  _generateMarkup() {
    return `
      <figure class="recipe__fig">
        <img src="${this._data.image}" alt="${this._data.title}" class="recipe__img" />
        <h1 class="recipe__title">
          <span>${this._data.title}</span>
        </h1>
      </figure>

      <div class="recipe__details">
        <div class="recipe__info">
          <svg class="recipe__info-icon">
            <use href="${icons}#icon-clock"></use>
          </svg>
          <span class="recipe__info-data recipe__info-data--minutes">${this._data.cookingTime}</span>
          <span class="recipe__info-text">minutes</span>
        </div>
        <div class="recipe__info">
          <svg class="recipe__info-icon">
            <use href="${icons}#icon-users"></use>
          </svg>
          <span class="recipe__info-data recipe__info-data--people">${this._data.servings}</span>
          <span class="recipe__info-text">servings</span>

          <div class="recipe__info-buttons">
            <button class="btn--tiny btn--increase-servings" data-update-to="${this._data.servings - 1}">
              <svg>
                <use href="${icons}#icon-minus-circle"></use>
              </svg>
            </button>
            <button class="btn--tiny btn--increase-servings" data-update-to="${this._data.servings + 1}">
              <svg>
                <use href="${icons}#icon-plus-circle"></use>
              </svg>
            </button>
          </div>
        </div>

        <div class="recipe__user-generated ${this._data.key ? '' : 'hidden'}">
          <svg>
            <use href="${icons}#icon-user"></use>
          </svg>
        </div>
        <button class="btn--round btn--bookmark">
          <svg class="">
            <use href="${icons}#icon-bookmark${this._data.bookmarked ? '-fill' : ''}"></use>
          </svg>
        </button>
      </div>

      <div class="recipe__ingredients">
        <h2 class="heading--2">Recipe ingredients</h2>
        <ul class="recipe__ingredient-list">
          ${this._data.ingredients.map(this._generateMarkupIngredient).join('')}
        </ul>
      </div>

      <div class="recipe__directions">
        <h2 class="heading--2">How to cook it</h2>
        <p class="recipe__directions-text">
          This recipe was carefully designed and tested by
          <span class="recipe__publisher">${this._data.publisher}</span>. Please check out
          directions at their website.
        </p>
        <a
          class="btn--small recipe__btn"
          href="${this._data.sourceUrl}"
          target="_blank"
        >
          <span>Directions</span>
          <svg class="search__icon">
            <use href="${icons}#icon-arrow-right"></use>
          </svg>
        </a>
      </div>
    `;
  }

  _generateMarkupIngredient(ing) {
    return `
      <li class="recipe__ingredient">
        <svg class="recipe__icon">
          <use href="${icons}#icon-check"></use>
        </svg>
        <div class="recipe__quantity">${ing.quantity ? ing.quantity : ''}</div>
        <div class="recipe__description">
          <span class="recipe__unit">${ing.unit}</span>
          ${ing.description}
        </div>
      </li>
    `;
  }
}

export default new RecipeView();

